// Lib
import React, { useCallback, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { useSelector } from 'react-redux';

// Utils
import { markEventAsHandled } from '../../utils/react/reactEventUtil';

// Selectors
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { userLocalePreferenceSelector } from '../../user/currentUserSelector';

// Actions
import { registerTiptapEditor, setActiveTiptapEditor, unregisterTiptapEditor } from './store/tiptapEditorStoreActions';

// Hooks
import useElementTiptapEditor, {
    UseTiptapEditorProps,
    UseTiptapEditorReturn,
} from '../../../common/tiptap/useElementTiptapEditor';
import { useTiptapEditorDispatch } from './store/tiptapEditorStoreHooks';
import { getQuotationCssCustomProperties } from '../../../common/tiptap/utils/quotationCssCustomProperties';

/**
 * The common editor's result plus the mousedown handler, which belongs on this side of the
 * common/client boundary because the "handled" marker it writes is an `ElementContainer` protocol.
 */
export type UseClientTiptapEditorReturn = UseTiptapEditorReturn & {
    onMouseDown: (event: React.MouseEvent) => void;
};

/**
 * Dispatches an editor update to the Tiptap editor redux store.
 */
const useDispatchTiptapActiveEditorStoreUpdate = () => {
    const dispatch = useTiptapEditorDispatch();

    return useCallback(
        (editor: Editor | null) => {
            dispatch(setActiveTiptapEditor(editor));
        },
        [dispatch],
    );
};

/**
 * Keeps the `ElementContainer` out of mousedowns that land inside the editor being edited, which
 * would otherwise clear the selection and leave editing mode.
 *
 * Marks the event rather than stopping its propagation, because the two are not interchangeable
 * here: a task item row is a React node view, so React dispatches the row's events from the row's
 * own `li` - inside the editor's DOM. Stopping propagation there stops the *native* event too, so
 * it never reaches ProseMirror's own listener on the editor DOM and ProseMirror's mouse handling
 * (e.g. triple-click selects the line) never runs. `ElementContainer.onMouseDown` skips events
 * already marked handled, so marking achieves the same thing without silencing ProseMirror.
 */
const useEditorMouseDownHandler = (isEditingThisEditor: boolean) =>
    useCallback(
        (event: React.MouseEvent) => {
            if (!isEditingThisEditor) return;
            markEventAsHandled(event);
        },
        [isEditingThisEditor],
    );

/**
 * Configures the Tiptap editor for use in the client.
 *
 * Thin wrapper around useElementTiptapEditor, which is in the common module and so can't include client code.
 */
export const useClientElementTiptapEditor = (
    elementTiptapEditorArgs: Omit<UseTiptapEditorProps, 'updateActiveEditorStore' | 'platformDetails'>,
): UseClientTiptapEditorReturn => {
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const localePreference = useSelector(userLocalePreferenceSelector);
    const quotationCssCustomProperties = getQuotationCssCustomProperties(localePreference);
    const dispatch = useTiptapEditorDispatch();
    const updateActiveEditorStore = useDispatchTiptapActiveEditorStoreUpdate();
    const editorResult = useElementTiptapEditor({
        ...elementTiptapEditorArgs,
        updateActiveEditorStore,
        platformDetails,
        quotationCssCustomProperties,
    });
    const { editor } = editorResult;
    const { registryEditorId, editorId, currentEditorId, isEditing } = elementTiptapEditorArgs;

    const onMouseDown = useEditorMouseDownHandler(currentEditorId === editorId && isEditing);

    useEffect(() => {
        if (!editor || !registryEditorId) return;

        dispatch(registerTiptapEditor(registryEditorId, editor));
        return () => {
            dispatch(unregisterTiptapEditor(registryEditorId, editor));
        };
    }, [dispatch, editor, registryEditorId]);

    return { ...editorResult, onMouseDown };
};

export default useClientElementTiptapEditor;
